import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useAtom } from "jotai";
import { Product } from "../../../features/products/product";
import { productDbAtom } from "../../../features/products/product.state";
import {
  BundleComposition,
  BundleComponent,
  computeComponentMax,
  emptyBundleComposition,
  getBundleComposition,
  getSlotStock,
  readStockQuantity,
  setBundleComposition,
  validateBundle,
} from "../../../features/products/bundle";
import { BundleSlotPicker } from "./BundleSlotPicker";
import { ProductEditModal } from "./ProductEditModal";
import { useDialogs } from "../../../contexts/useDialogs";

interface BundleCompositionTabProps {
  product: Product;
  onChange: (next: Product) => void;
}

export const BundleCompositionTab = ({
  product,
  onChange,
}: BundleCompositionTabProps) => {
  const [productDb] = useAtom(productDbAtom);
  const { productModalApi } = useDialogs();
  const [editComponentId, setEditComponentId] = useState<string | null>(null);
  const composition = getBundleComposition(product);
  const isMarkedBundle = !!composition?.isBundle;
  const variantCount = product.variants?.length ?? 0;
  const hasVariants = variantCount > 0;

  const updateComposition = useCallback(
    (next: BundleComposition | null) => {
      onChange(setBundleComposition(product, next));
    },
    [onChange, product],
  );

  const toggleBundle = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        updateComposition(null);
        return;
      }
      updateComposition(composition ?? emptyBundleComposition());
    },
    [composition, updateComposition],
  );

  const addSlot = useCallback(() => {
    const next: BundleComposition = composition
      ? {
          ...composition,
          components: [
            ...composition.components,
            { itemId: "", variantId: 0, quantity: 1 },
          ],
        }
      : {
          ...emptyBundleComposition(),
          components: [{ itemId: "", variantId: 0, quantity: 1 }],
        };
    updateComposition(next);
  }, [composition, updateComposition]);

  const updateSlot = useCallback(
    (index: number, slot: BundleComponent) => {
      if (!composition) return;
      const components = composition.components.map((c, i) =>
        i === index ? slot : c,
      );
      updateComposition({ ...composition, components });
    },
    [composition, updateComposition],
  );

  const removeSlot = useCallback(
    (index: number) => {
      if (!composition) return;
      const components = composition.components.filter((_, i) => i !== index);
      updateComposition({ ...composition, components });
    },
    [composition, updateComposition],
  );

  const validationError = useMemo(
    () => validateBundle(product, productDb),
    [product, productDb],
  );

  // Stock summary — read directly from product.fields["Stock Quantity"] and from
  // each component's effective stock. Reading the live field (not the saved
  // inStock boolean) means edits made on the Product Fields tab show up here
  // immediately, mirroring InventoryList's behavior.
  const stockSummary = useMemo(() => {
    if (!isMarkedBundle || !composition || composition.components.length === 0) {
      return null;
    }
    const hasUntracked = composition.components.some(
      (slot) => slot.itemId && getSlotStock(slot, productDb).status === "unknown",
    );
    return {
      ownStock: readStockQuantity(product, productDb),
      componentMax: computeComponentMax(composition, productDb),
      hasUntracked,
    };
  }, [composition, isMarkedBundle, product, productDb]);

  // Bundles and variants are mutually exclusive — a bundle IS the composition;
  // adding "variants" of a bundle doesn't make conceptual sense in Phase 1.
  // If the product already has variants, the bundle toggle is disabled with
  // a tooltip explaining how to switch over.
  const toggleDisabled = !isMarkedBundle && hasVariants;

  return (
    <Box sx={{ pt: 1 }}>
      <Tooltip
        title={
          toggleDisabled
            ? `Remove the ${variantCount} variant${variantCount === 1 ? "" : "s"} on the Product Variants tab first — a bundle can't also have its own variants.`
            : ""
        }
      >
        <FormControlLabel
          control={
            <Switch
              checked={isMarkedBundle}
              onChange={(e) => toggleBundle(e.target.checked)}
              disabled={toggleDisabled}
            />
          }
          label="This product is a bundle"
        />
      </Tooltip>

      {isMarkedBundle && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            A bundle is sold as a single product but ships as the combined components below.
            The bundle's own price lives on the Product Fields tab (Price, Dozen, etc.).
            Customer sees one line on the order; admin order detail shows the expanded items.
          </Typography>

          {validationError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          {stockSummary && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                <b>Bundle stock:</b>{" "}
                {stockSummary.ownStock === null ? (
                  <Box component="span" sx={{ color: "text.disabled" }}>
                    not tracked
                  </Box>
                ) : (
                  <Box
                    component="span"
                    sx={{
                      color:
                        stockSummary.ownStock > 0
                          ? "success.main"
                          : "warning.main",
                      fontWeight: 600,
                    }}
                  >
                    {stockSummary.ownStock}
                  </Box>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <b>Max from components:</b>{" "}
                {stockSummary.componentMax === null ? (
                  <Box component="span" sx={{ color: "text.disabled" }}>
                    n/a
                  </Box>
                ) : (
                  <Box
                    component="span"
                    sx={{
                      color:
                        stockSummary.componentMax > 0
                          ? "success.main"
                          : "warning.main",
                      fontWeight: 600,
                    }}
                  >
                    {stockSummary.componentMax}
                  </Box>
                )}
              </Typography>
            </Box>
          )}

          {stockSummary?.hasUntracked && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              A component has no Stock Quantity. If you manage inventory, an
              untracked component is treated as out of stock and the bundle won't
              be orderable — set a Stock Quantity on it (a unit is a unit whether
              sold alone or in a bundle).
            </Typography>
          )}

          <Button
            startIcon={<AddIcon />}
            onClick={addSlot}
            size="small"
            sx={{ mb: 1 }}
          >
            Add component
          </Button>

          <Stack spacing={0}>
            {composition?.components.map((slot, idx) => (
              <BundleSlotPicker
                key={idx}
                slot={slot}
                bundleItemId={product.id}
                onChange={(next) => updateSlot(idx, next)}
                onRemove={() => removeSlot(idx)}
                onEdit={(componentItemId) => setEditComponentId(componentItemId)}
              />
            ))}
          </Stack>
        </>
      )}

      {/*
        Nested edit modal for components. Renders on top of the bundle's own
        ProductEditModal so the bundle composition stays preserved while the user
        peeks at or edits the component. shouldUpdateQueryString stays off — the
        URL belongs to the outer (bundle) modal.
      */}
      {editComponentId && (
        <ProductEditModal
          productId={editComponentId}
          openDeleteProducts={productModalApi.openDeleteProducts}
          onClose={() => setEditComponentId(null)}
          shouldUpdateQueryString={false}
        />
      )}
    </Box>
  );
};

